import {Tooltip} from 'platform/components';
import {Text} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {MarketMetrics, PRICE_LADDER_PAGE_COUNT} from '../utils/computeMarketMetrics';
import {MetricsSkeleton} from './MetricsSkeleton';

const EMPTY_VALUE = '–';

interface PriceLadderProps extends TestIdProps {
  metrics: MarketMetrics | null;
  isLoading: boolean;
  formatPrice: (price: number | null) => string;
}

type LadderVariant = 'step' | 'max';

/** Progressively darkening neutral shades: min → 3rd page. */
const STEP_SHADES = [10, 20, 30, 40] as const;

type LadderStep = {
  key: string;
  label: string;
  tooltip: string;
  value: number | null;
  variant: LadderVariant;
  /** Neutral palette shade for the 'step' variant background. */
  shade: (typeof STEP_SHADES)[number];
};

function buildSteps(metrics: MarketMetrics | null): LadderStep[] {
  const pageEndPrices = metrics?.pageEndPrices ?? Array(PRICE_LADDER_PAGE_COUNT).fill(null);
  return [
    {
      key: 'min',
      label: i18n.t('entity.filterForm.savedFiltersPanel.ladder.min'),
      tooltip: i18n.t('entity.filterForm.savedFiltersPanel.tooltip.ladderMin'),
      value: metrics?.advertised.minPrice ?? null,
      variant: 'step',
      shade: STEP_SHADES[0],
    },
    ...pageEndPrices.map(
      (price, index): LadderStep => ({
        key: `page-${index + 1}`,
        label: i18n.t('entity.filterForm.savedFiltersPanel.ladder.page', {page: index + 1}),
        tooltip: i18n.t('entity.filterForm.savedFiltersPanel.tooltip.ladderPage', {
          page: index + 1,
        }),
        value: price,
        variant: 'step',
        shade: STEP_SHADES[Math.min(index + 1, STEP_SHADES.length - 1)],
      })
    ),
    {
      key: 'max',
      label: i18n.t('entity.filterForm.savedFiltersPanel.ladder.max'),
      tooltip: i18n.t('entity.filterForm.savedFiltersPanel.tooltip.ladderMax'),
      value: metrics?.advertised.maxPrice ?? null,
      variant: 'max',
      shade: STEP_SHADES[0],
    },
  ];
}

/**
 * “Prices in advertisement” ladder: cheapest car, price at the end of each ads listing
 * page (20 cars per page) and the most expensive car.
 */
export function PriceLadder(props: PriceLadderProps) {
  const steps = buildSteps(props.metrics);

  return (
    <LadderRow data-testid={suffixTestId('priceLadder', props)}>
      {steps.map((step) => (
        <Tooltip key={step.key} label={step.tooltip} placement="top">
          <StepBox
            $variant={step.variant}
            $shade={step.shade}
            $isEmpty={isNil(step.value)}
            data-testid={suffixTestId(`priceLadder-${step.key}`, props)}
          >
            {props.isLoading ? (
              <MetricsSkeleton width="56px" height={4} />
            ) : (
              <FluidAmount>
                <Text
                  size="xSmall"
                  alternative
                  noWrap
                  color={
                    step.variant === 'max' ? 'white' : isNil(step.value) ? 'tertiary' : undefined
                  }
                >
                  {isNil(step.value) ? EMPTY_VALUE : props.formatPrice(step.value)}
                </Text>
              </FluidAmount>
            )}
            <Text size="xxSmall" color={step.variant === 'max' ? 'white' : 'tertiary'} noWrap>
              {step.label}
            </Text>
          </StepBox>
        </Tooltip>
      ))}
    </LadderRow>
  );
}

const LadderRow = styled.div`
  display: flex;
  gap: ${({theme}) => theme.getSize(1)};
  align-items: stretch;

  /* Stacked layout: the flex items are the Tooltip wrappers around the boxes —
     they share the full row width equally. */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    width: 100%;

    > * {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

/**
 * Thin wrapper around the amount Text: on stacked layouts the boxes share the
 * screen width, so only the font size turns fluid (between the xxSmall and xSmall
 * theme sizes) — every other typography token still comes from Text itself.
 */
const FluidAmount = styled.div`
  min-width: 0;

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    > div {
      font-size: clamp(
        ${({theme}) => theme.fontSizes.text.xxSmall},
        2.6vw,
        ${({theme}) => theme.fontSizes.text.xSmall}
      );
    }
  }
`;

const StepBox = styled.div<{
  $variant: LadderVariant;
  $shade: (typeof STEP_SHADES)[number];
  $isEmpty: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({theme}) => theme.getSize(1)};
  /* Fixed width sized for seven-digit prices (“1 073 184 Kč”) so box widths
     never jump between rows; only rarer, longer amounts may still expand. */
  min-width: ${({theme}) => theme.getSize(28)};
  flex-shrink: 0;
  padding: ${({theme}) => `${theme.getSize(1)} ${theme.getSize(2)}`};
  border-radius: ${({theme}) => theme.radii.small};

  /* Stacked layout: the five boxes share the screen width instead of scrolling —
     full width of their Tooltip wrapper, tighter padding, fluid amount font. */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    min-width: 0;
    width: 100%;
    height: 100%;
    padding: ${({theme}) => theme.getSize(1)};
    overflow: hidden;
  }

  ${({theme, $variant, $shade, $isEmpty}) => {
    if ($variant === 'max') {
      return css`
        background-color: ${theme.colors.palettes.neutral[600][100]};
      `;
    }
    if ($isEmpty) {
      return css`
        border: 1px dashed ${theme.colors.palettes.neutral[40][100]};
        background-color: transparent;
      `;
    }
    return css`
      background-color: ${theme.colors.palettes.neutral[$shade][100]};
    `;
  }}
`;
